import React, { FC, useEffect, useState } from 'react';
import { Box, Skeleton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CrosspostEmbed, { CrosspostOriginal } from './CrosspostEmbed';
import postService from '../services/posts';

// Cache resolved originals so re-renders and repeated links don't refetch.
const cache = new Map<string, CrosspostOriginal | null>();

interface InAppPostEmbedProps {
  id: string;
}

/**
 * Renders an in-app post link as a rich attributed embed (the shared post's
 * "preview"). Resolves the linked post once and caches it; shows a graceful
 * fallback if it was removed.
 */
const InAppPostEmbed: FC<InAppPostEmbedProps> = ({ id }) => {
  const theme = useTheme();
  const [original, setOriginal] = useState<CrosspostOriginal | null | undefined>(
    cache.has(id) ? cache.get(id) : undefined
  );

  useEffect(() => {
    if (cache.has(id)) {
      setOriginal(cache.get(id));
      return;
    }
    let active = true;
    postService
      .getPostComments(id)
      .then((p) => {
        const resolved: CrosspostOriginal = {
          id: p.id,
          title: p.title,
          herd: p.herd,
          author: p.author,
          media: p.media,
          commentCount: p.commentCount,
          trueReactionCount: p.trueReactionCount,
          falseReactionCount: p.falseReactionCount,
        };
        cache.set(id, resolved);
        if (active) setOriginal(resolved);
      })
      .catch(() => {
        cache.set(id, null);
        if (active) setOriginal(null);
      });
    return () => {
      active = false;
    };
  }, [id]);

  if (original === undefined) {
    return (
      <Box sx={{ mt: 1 }}>
        <Skeleton
          variant="rounded"
          height={92}
          sx={{ borderRadius: 'var(--sh-lg)', bgcolor: theme.tokens.colors.backgroundCardHover }}
        />
      </Box>
    );
  }

  // Removed original: show the attribution shell's "no longer available" state.
  return (
    <Box sx={{ mt: 1 }}>
      <CrosspostEmbed original={original} />
    </Box>
  );
};

export default InAppPostEmbed;
